import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminGetProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminGetImageUploadUrl, adminGetCategories, adminCreateCategory } from '../../api/products';
import styles from './ProductsPage.module.css';
import axios from 'axios';

interface ProductForm {
  name: string; nameTA: string;
  description: string; descriptionTA: string;
  categoryId: string; price: string;
  fabric: string; colour: string;
  occasion: string; isFeatured: boolean; isActive: boolean;
  images: string[];
}

const EMPTY_FORM: ProductForm = {
  name: '', nameTA: '', description: '', descriptionTA: '',
  categoryId: '', price: '', fabric: '', colour: '', occasion: '',
  isFeatured: false, isActive: true, images: [],
};

export default function ProductsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState<ProductForm>(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);

  const { data } = useQuery({ queryKey: ['admin-products'], queryFn: adminGetProducts });
  const { data: catData } = useQuery({ queryKey: ['admin-categories'], queryFn: adminGetCategories });

  const createMutation = useMutation({
    mutationFn: (d: unknown) => adminCreateProduct(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-products'] }); setModal(null); toast.success('Product created'); },
    onError: () => toast.error('Failed to create product'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) => adminUpdateProduct(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-products'] }); setModal(null); toast.success('Product updated'); },
    onError: () => toast.error('Failed to update product'),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteProduct,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-products'] }); toast.success('Product deleted'); },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { uploadUrl, imageUrl } = await adminGetImageUploadUrl(file.name, file.type);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      setForm(f => ({ ...f, images: [...f.images, imageUrl] }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price),
      occasion: form.occasion.split(',').map(s => s.trim()).filter(Boolean),
    };
    if (modal === 'create') createMutation.mutate(payload);
    else if (editing) updateMutation.mutate({ id: editing.id as string, data: payload });
  };

  const openEdit = (p: Record<string, unknown>) => {
    setEditing(p);
    setForm({
      name: p.name as string || '', nameTA: p.nameTA as string || '',
      description: p.description as string || '', descriptionTA: p.descriptionTA as string || '',
      categoryId: p.categoryId as string || '', price: String(p.price || ''),
      fabric: p.fabric as string || '', colour: p.colour as string || '',
      occasion: ((p.occasion as string[]) || []).join(', '),
      isFeatured: p.isFeatured as boolean || false,
      isActive: p.isActive !== false,
      images: p.images as string[] || [],
    });
    setModal('edit');
  };

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>{t('admin.products')}</h1>
        <button className={styles.addBtn} onClick={() => { setForm(EMPTY_FORM); setEditing(null); setModal('create'); }}>
          <Plus size={16} /> {t('admin.add_product')}
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Image</th><th>Name</th><th>Category</th><th>Price</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.products || []).map((p: Record<string, unknown>) => (
              <tr key={p.id as string}>
                <td>
                  {(p.images as string[])?.[0]
                    ? <img src={(p.images as string[])[0]} alt="" className={styles.thumb} />
                    : <div className={styles.thumbPlaceholder} />
                  }
                </td>
                <td>
                  <div className={styles.productName}>{p.name as string}</div>
                  <div className={styles.productFabric}>{p.fabric as string}</div>
                </td>
                <td>{p.categoryId as string}</td>
                <td className={styles.price}>₹{(p.price as number)?.toLocaleString('en-IN')}</td>
                <td>
                  <span className={p.isActive ? styles.activeTag : styles.inactiveTag}>
                    {p.isActive ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className={styles.actions}>
                  <button className={styles.editBtn} onClick={() => openEdit(p)}><Edit2 size={15} /></button>
                  <button className={styles.deleteBtn} onClick={() => { if (confirm('Delete?')) deleteMutation.mutate(p.id as string); }}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className={styles.overlay} onClick={() => setModal(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>{modal === 'create' ? 'Add Product' : 'Edit Product'}</h2>
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.grid2}>
                <div className={styles.field}><label>Name (English) *</label><input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required /></div>
                <div className={styles.field}><label>பெயர் (Tamil)</label><input value={form.nameTA} onChange={e => setForm(f => ({...f, nameTA: e.target.value}))} /></div>
              </div>
              <div className={styles.field}><label>Description</label><textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} /></div>
              <div className={styles.field}><label>விவரம் (Tamil)</label><textarea value={form.descriptionTA} onChange={e => setForm(f => ({...f, descriptionTA: e.target.value}))} rows={3} /></div>
              <div className={styles.grid2}>
                <div className={styles.field}>
                  <label>Category *</label>
                  <select value={form.categoryId} onChange={e => setForm(f => ({...f, categoryId: e.target.value}))} required>
                    <option value="">Select...</option>
                    {(catData?.categories || []).map((c: { id: string; name: string }) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className={styles.field}><label>Price (₹) *</label><input type="number" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} required min="0" /></div>
              </div>
              <div className={styles.grid3}>
                <div className={styles.field}><label>Fabric</label><input value={form.fabric} onChange={e => setForm(f => ({...f, fabric: e.target.value}))} /></div>
                <div className={styles.field}><label>Colour</label><input value={form.colour} onChange={e => setForm(f => ({...f, colour: e.target.value}))} /></div>
                <div className={styles.field}><label>Occasion (comma separated)</label><input value={form.occasion} onChange={e => setForm(f => ({...f, occasion: e.target.value}))} /></div>
              </div>
              <div className={styles.checkboxes}>
                <label><input type="checkbox" checked={form.isFeatured} onChange={e => setForm(f => ({...f, isFeatured: e.target.checked}))} /> Featured</label>
                <label><input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({...f, isActive: e.target.checked}))} /> Active (visible)</label>
              </div>
              <div className={styles.field}>
                <label>Images</label>
                <div className={styles.imageUpload}>
                  {form.images.map((img, i) => <img key={i} src={img} alt="" className={styles.uploadedImg} />)}
                  <label className={styles.uploadBtn}>
                    <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Image'}
                    <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} hidden />
                  </label>
                </div>
              </div>
              <div className={styles.modalActions}>
                <button type="button" onClick={() => setModal(null)} className={styles.cancelBtn}>Cancel</button>
                <button type="submit" className={styles.saveBtn} disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
